/**
 * Унифицированный обработчик ошибок
 */

/**
 * Коды ошибок чата
 */
export enum ChatErrorCode {
  SEND_MESSAGE_FAILED = 'SEND_MESSAGE_FAILED',
  FETCH_CHAT_FAILED = 'FETCH_CHAT_FAILED',
  FETCH_CHATS_FAILED = 'FETCH_CHATS_FAILED',
  FETCH_MESSAGES_FAILED = 'FETCH_MESSAGES_FAILED',
  UPDATE_MESSAGE_FAILED = 'UPDATE_MESSAGE_FAILED',
  DELETE_MESSAGE_FAILED = 'DELETE_MESSAGE_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVER_ERROR = 'SERVER_ERROR',
  CHAT_NOT_FOUND = 'CHAT_NOT_FOUND',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
}

/**
 * Класс ошибки чата
 */
export class ChatError extends Error {
  constructor(
    message: string,
    public code: ChatErrorCode,
    public originalError?: unknown,
  ) {
    super(message)
    this.name = 'ChatError'
  }
}

export interface ErrorInfo {
  type: 'chat' | 'network' | 'validation' | 'unknown'
  code?: string | number
  message: string
  originalError?: unknown
}

/**
 * Сервис для логирования ошибок
 * Упрощенная версия для внутреннего приложения
 */
class ErrorLogger {
  /**
   * Логирует ошибку с группировкой в dev режиме
   */
  static log(error: Error, context: string): void {
    if (import.meta.env.PROD) {
      // В prod минимальное логирование
      console.error(`[Chat] ${context}:`, error.message)

      return
    }

    // В dev подробное логирование с группировкой
    console.group(`🔴 [Chat Error] ${context}`)
    console.error('Error:', error)
    if (error.stack) console.error('Stack:', error.stack)
    console.groupEnd()
  }
}

/**
 * Обрабатывает ошибку и возвращает информацию для UI
 */
export class ErrorHandler {
  /**
   * Обрабатывает ошибку и возвращает информацию для отображения пользователю
   * Также отправляет ошибку в систему мониторинга
   */
  static handle(error: unknown, context: string): ErrorInfo {
    // Логируем ошибку
    if (error instanceof Error) ErrorLogger.log(error, context)
    else if (error instanceof ChatError) ErrorLogger.log(error, context)
    else ErrorLogger.log(new Error(String(error)), context)

    if (error instanceof ChatError) {
      return {
        type: 'chat',
        code: error.code,
        message: this.getLocalizedMessage(error.code),
        originalError: error,
      }
    }

    const apiDetail = this.getApiErrorDetail(error)
    if (apiDetail) {
      return {
        type: 'unknown',
        message: apiDetail,
        originalError: error,
      }
    }

    // Проверяем на сетевые ошибки
    if (this.isNetworkError(error)) {
      return {
        type: 'network',
        code: this.getNetworkErrorCode(error),
        message: this.getNetworkErrorMessage(error),
        originalError: error,
      }
    }

    // Проверяем на ошибки валидации
    if (this.isValidationError(error)) {
      return {
        type: 'validation',
        message: this.getValidationErrorMessage(error),
        originalError: error,
      }
    }

    return {
      type: 'unknown',
      message: 'Произошла неизвестная ошибка. Пожалуйста, попробуйте еще раз.',
      originalError: error,
    }
  }

  private static getApiErrorDetail(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null
    }

    const data = (error as { response?: { data?: unknown } }).response?.data
    if (!data || typeof data !== 'object') {
      return null
    }

    const errors = (data as { errors?: Array<{ detail?: unknown }> }).errors
    if (Array.isArray(errors)) {
      const details = errors
        .map(item => (typeof item?.detail === 'string' ? item.detail.trim() : ''))
        .filter(Boolean)

      if (details.length) {
        return details.join('\n')
      }
    }

    const detail = (data as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim()
    }

    return null
  }

  /**
   * Получает локализованное сообщение по коду ошибки
   */
  private static getLocalizedMessage(code: ChatErrorCode): string {
    const messages: Partial<Record<ChatErrorCode, string>> = {
      [ChatErrorCode.SEND_MESSAGE_FAILED]: 'Не удалось отправить сообщение',
      [ChatErrorCode.FETCH_CHAT_FAILED]: 'Не удалось загрузить чат',
      [ChatErrorCode.NETWORK_ERROR]:
        'Ошибка сети. Проверьте подключение к интернету',
      [ChatErrorCode.INVALID_INPUT]: 'Некорректные данные',
      [ChatErrorCode.FETCH_CHATS_FAILED]: 'Не удалось загрузить чаты',
      [ChatErrorCode.FETCH_MESSAGES_FAILED]: 'Не удалось загрузить сообщения',
      [ChatErrorCode.UPDATE_MESSAGE_FAILED]: 'Не удалось обновить сообщение',
      [ChatErrorCode.DELETE_MESSAGE_FAILED]: 'Не удалось удалить сообщение',
      [ChatErrorCode.UNAUTHORIZED]: 'Необходима авторизация',
      [ChatErrorCode.FORBIDDEN]: 'Недостаточно прав для выполнения операции',
      [ChatErrorCode.NOT_FOUND]: 'Ресурс не найден',
      [ChatErrorCode.SERVER_ERROR]: 'Ошибка сервера. Попробуйте позже',
      [ChatErrorCode.CHAT_NOT_FOUND]: 'Чат не найден',
      [ChatErrorCode.RATE_LIMIT_EXCEEDED]:
        'Слишком много запросов. Пожалуйста, подождите немного',
    }

    return messages[code] || 'Произошла ошибка'
  }

  /**
   * Проверяет, является ли ошибка сетевой
   */
  private static isNetworkError(error: unknown): boolean {
    if (error && typeof error === 'object') {
      interface NetworkError {
        name?: string
        message?: string
        code?: string
        response?: unknown
        request?: unknown
      }
      const err = error as NetworkError

      return (
        err.name === 'NetworkError'
        || err.message?.includes('network')
        || err.message?.includes('Network')
        || err.code === 'NETWORK_ERROR'
        || (err.response === undefined && err.request !== undefined)
      )
    }

    return false
  }

  /**
   * Получает код сетевой ошибки
   */
  private static getNetworkErrorCode(error: unknown): number | string {
    if (error && typeof error === 'object') {
      interface NetworkError {
        response?: { status: number }
        code?: string
      }
      const err = error as NetworkError

      return err.response?.status || err.code || 'NETWORK_ERROR'
    }

    return 'NETWORK_ERROR'
  }

  /**
   * Получает сообщение сетевой ошибки
   */
  private static getNetworkErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      interface NetworkError {
        response?: { status: number }
      }
      const err = error as NetworkError
      const status = err.response?.status

      if (status === 404) return 'Ресурс не найден'
      if (status === 403) return 'Доступ запрещен'
      if (status === 401) return 'Необходима авторизация'
      if (status !== undefined && status >= 500) {
        return 'Ошибка сервера. Попробуйте позже'
      }
      if (status !== undefined && status >= 400) return 'Ошибка запроса'
    }

    return 'Ошибка сети. Проверьте подключение к интернету'
  }

  /**
   * Проверяет, является ли ошибка ошибкой валидации
   */
  private static isValidationError(error: unknown): boolean {
    if (error && typeof error === 'object') {
      interface ValidationError {
        code?: string
        message?: string
      }
      const err = error as ValidationError

      return !!(
        err.code === ChatErrorCode.INVALID_INPUT
        || err.message?.includes('validation')
        || err.message?.includes('invalid')
        || err.message?.includes('валидация')
      )
    }

    return false
  }

  /**
   * Получает сообщение ошибки валидации
   */
  private static getValidationErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      interface ValidationError {
        message?: string
      }
      const err = error as ValidationError
      if (err.message) return err.message
    }

    return 'Некорректные данные'
  }
}

/**
 * Обрабатывает ошибки чата с логированием и пробрасыванием
 *
 * @param error - Ошибка для обработки
 * @param operation - Операция, во время которой произошла ошибка
 * @throws ChatError
 */
export function handleChatError(error: unknown, operation: string): never {
  // Логируем ошибку
  if (error instanceof Error) ErrorLogger.log(error, operation)

  if (error instanceof ChatError) throw error

  if (error instanceof Error) {
    throw new ChatError(error.message, ChatErrorCode.NETWORK_ERROR, error)
  }

  throw new ChatError('Неизвестная ошибка', ChatErrorCode.NETWORK_ERROR, error)
}
